// Considered: No existing molecule displays the 4-stat grid (defaultLimit, maxDepth, permissionCheck, ancestors) with a read-only cache strip. See spec Reuse Audit.
import React from 'react';
import { useTranslation } from 'localization';
import { InputNumberAtom } from '../../atoms/InputNumber/InputNumberAtom';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import SwitchAtom from '../../atoms/Switch/SwitchAtom';
import type { ContextFooterDef } from '../../../types/contextFlow';
import './ContextFooterSettingsMolecule.scss';

export interface ContextFooterSettingsMoleculeProps {
  settings: ContextFooterDef;
  onChange: (settings: ContextFooterDef) => void;
  readOnly?: boolean;
}

const ContextFooterSettingsMolecule: React.FC<ContextFooterSettingsMoleculeProps> = ({
  settings,
  onChange,
  readOnly = false,
}) => {
  const { t } = useTranslation();

  const permissionOptions = [
    { label: t('context.editor.permissionView'), value: 'view' as const },
    { label: t('context.editor.permissionEdit'), value: 'edit' as const },
    { label: t('context.editor.permissionAdmin'), value: 'admin' as const },
  ];

  const patch = (partial: Partial<ContextFooterDef>) => onChange({ ...settings, ...partial });

  return (
    <div className="context-footer-settings-molecule">
      {/* Editable stats */}
      <div className="context-footer-settings-molecule__grid">
        <div className="context-footer-settings-molecule__stat">
          <div className="context-footer-settings-molecule__stat-label">
            {t('context.editor.footer.defaultLimit')}
          </div>
          <div className="context-footer-settings-molecule__stat-input-row">
            <InputNumberAtom
              value={settings.defaultLimit}
              onChange={(v) => patch({ defaultLimit: v ?? 50 })}
              disabled={readOnly}
              min={1}
              max={500}
              type="integer"
              className="context-footer-settings-molecule__input"
            />
            <div className="context-footer-settings-molecule__stat-helper">
              {t('context.editor.footer.defaultLimitHelper', 'per level')}
            </div>
          </div>
        </div>

        <div className="context-footer-settings-molecule__stat">
          <div className="context-footer-settings-molecule__stat-label">
            {t('context.editor.footer.maxDepth')}
          </div>
          <div className="context-footer-settings-molecule__stat-input-row">
            <InputNumberAtom
              value={settings.maxDepth}
              onChange={(v) => patch({ maxDepth: v ?? 5 })}
              disabled={readOnly}
              min={1}
              max={20}
              type="integer"
              className="context-footer-settings-molecule__input"
            />
            <div className="context-footer-settings-molecule__stat-helper">
              {t('context.editor.footer.maxDepthHelper', 'safety cap')}
            </div>
          </div>
        </div>

        <div className="context-footer-settings-molecule__stat">
          <div className="context-footer-settings-molecule__stat-label">
            {t('context.editor.footer.permissionCheck')}
          </div>
          <div className="context-footer-settings-molecule__stat-input-row">
            <DropdownAtom
              value={settings.permissionCheck}
              options={permissionOptions}
              onChange={(e) => patch({ permissionCheck: e.value })}
              disabled={readOnly}
              className="context-footer-settings-molecule__select"
            />
            <div className="context-footer-settings-molecule__stat-helper">
              {t('context.editor.footer.permissionCheckHelper', 'minimum to surface')}
            </div>
          </div>
        </div>

        <div className="context-footer-settings-molecule__stat">
          <div className="context-footer-settings-molecule__stat-label">
            {t('context.editor.footer.includeAncestors')}
          </div>
          <div className="context-footer-settings-molecule__toggle-row">
            <SwitchAtom
              checked={!!settings.includeAncestors}
              onChange={(checked) => patch({ includeAncestors: checked })}
              disabled={readOnly}
              aria-label={t('context.editor.footer.includeAncestors')}
            />
            <span className="context-footer-settings-molecule__toggle-state">
              {settings.includeAncestors
                ? t('context.editor.footer.on')
                : t('context.editor.footer.off')}
            </span>
          </div>
        </div>
      </div>

      {/* System-managed strip (cache) — read-only */}
      {settings.cacheTtlMin !== undefined && (
        <div className="context-footer-settings-molecule__system-strip">
          <i className="pi pi-lock" aria-hidden="true" />
          <span className="context-footer-settings-molecule__system-label">
            {t('context.editor.footer.system')}
          </span>
          <span
            className="context-footer-settings-molecule__cache-chip"
            data-testid="footer-cache-display"
            aria-label={t('context.editor.footer.cacheLabel', { ttl: settings.cacheTtlMin })}
          >
            {t('context.editor.footer.cache')}{': '}
            <strong>{settings.cacheTtlMin}</strong>{' '}
            {t('context.editor.footer.cacheUnit')}
          </span>
          <span className="context-footer-settings-molecule__system-note">
            {t('context.editor.footer.cacheNote')}
          </span>
        </div>
      )}
    </div>
  );
};

export default ContextFooterSettingsMolecule;
